import * as Instancia from '../constantes/InstanciaPartido.js';
import * as DTOs from '../DTOs/PartidoFixtureDTOs.js';

/**
 * Calcula cuántos partidos se esperan en una etapa de definición
 * @param {Object} etapa - Objeto etapa con cantidadCruces y cantidadRuedas
 * @returns {number} Cantidad total de partidos esperados
 */
export const CantidadPartidosEsperadosDefinicion = (etapa) => {
  if (!etapa || !etapa.cantidadCruces || !etapa.cantidadRuedas) {
    return 0;
  }
  // En definición: cantidadCruces * cantidadRuedas
  // Si cantidadRuedas = 1 → cantidadCruces partidos únicos
  // Si cantidadRuedas = 2 → cantidadCruces partidos de ida + cantidadCruces partidos de vuelta
  return etapa.cantidadCruces * etapa.cantidadRuedas;
};

/**
 * Genera partidos vacíos para una etapa de definición
 * @param {Object} contexto - Datos del contexto (torneo, categoría, etapa)
 * @param {number} startId - ID temporal inicial (negativo) para los partidos generados
 * @returns {Array} Array de partidos vacíos
 */
export const GenerarPartidosDefinicion = (contexto, startId = -1) => {
  const etapa = contexto.etapa;
  if (!etapa || etapa.definicion !== true) {
    return [];
  }

  const partidos = [];
  const cantidadCruces = etapa.cantidadCruces || 0;
  const cantidadRuedas = etapa.cantidadRuedas || 1;
  let idTemporal = startId;

  if (cantidadRuedas === 1) {
    // Partidos únicos
    for (let i = 0; i < cantidadCruces; i++) {
      partidos.push(
        DTOs.PartidoFixtureVacio(contexto, idTemporal--, Instancia.UNICO, i, null, null)
      );
    }
  } else if (cantidadRuedas === 2) {
    // Partidos de ida
    for (let i = 0; i < cantidadCruces; i++) {
      partidos.push(DTOs.PartidoFixtureVacio(contexto, idTemporal--, Instancia.IDA, i, null, null));
    }
    // Partidos de vuelta
    for (let i = 0; i < cantidadCruces; i++) {
      partidos.push(
        DTOs.PartidoFixtureVacio(contexto, idTemporal--, Instancia.VUELTA, i, null, null)
      );
    }
  }

  return partidos;
};

/**
 * Identifica qué partidos faltan en una etapa de definición
 * @param {Array} partidosExistentes - Partidos ya creados en la base de datos
 * @param {Object} contexto - Datos del contexto (torneo, categoría, etapa)
 * @returns {Array} Array de partidos vacíos para los que faltan
 */
export const PartidosFaltantesDefinicion = (partidosExistentes, contexto) => {
  const etapa = contexto.etapa;
  if (!etapa || etapa.definicion !== true) {
    return [];
  }

  const existentes = partidosExistentes || [];
  const cantidadCruces = etapa.cantidadCruces || 0;
  const cantidadRuedas = etapa.cantidadRuedas || 1;

  // Contar cuántos partidos ya existen por instancia
  const contadores = {
    [Instancia.UNICO]: 0,
    [Instancia.IDA]: 0,
    [Instancia.VUELTA]: 0,
  };

  existentes.forEach((partido) => {
    if (partido.codigoInstancia && contadores[partido.codigoInstancia] !== undefined) {
      contadores[partido.codigoInstancia]++;
    }
  });

  const faltantes = [];
  let idTemporal = -1;

  if (cantidadRuedas === 1) {
    // Generar partidos únicos faltantes
    const faltantesUnicos = cantidadCruces - contadores[Instancia.UNICO];
    for (let i = 0; i < faltantesUnicos; i++) {
      faltantes.push(
        DTOs.PartidoFixtureVacio(contexto, idTemporal--, Instancia.UNICO, i, null, null)
      );
    }
  } else if (cantidadRuedas === 2) {
    // Generar partidos de ida faltantes
    const faltantesIda = cantidadCruces - contadores[Instancia.IDA];
    for (let i = 0; i < faltantesIda; i++) {
      faltantes.push(
        DTOs.PartidoFixtureVacio(contexto, idTemporal--, Instancia.IDA, i, null, null)
      );
    }
    // Generar partidos de vuelta faltantes
    const faltantesVuelta = cantidadCruces - contadores[Instancia.VUELTA];
    for (let i = 0; i < faltantesVuelta; i++) {
      faltantes.push(
        DTOs.PartidoFixtureVacio(contexto, idTemporal--, Instancia.VUELTA, i, null, null)
      );
    }
  }

  return faltantes;
};

/**
 * Agrupa partidos de definición por instancia (Único, Ida, Vuelta)
 * @param {Array} partidos - Array de partidos (existentes y vacíos)
 * @returns {Object} Objeto con keys IDA, VUELTA, UNICO y arrays de partidos
 */
export const AgruparPartidosPorInstancia = (partidos) => {
  const grupos = {
    [Instancia.UNICO]: [],
    [Instancia.IDA]: [],
    [Instancia.VUELTA]: [],
  };

  partidos.forEach((partido) => {
    const instancia = partido.codigoInstancia;
    if (grupos[instancia]) {
      grupos[instancia].push(partido);
    }
  });

  return grupos;
};

/**
 * Replica un equipo de partido de ida a partido de vuelta (invertido)
 * @param {Object} partidoIda - Partido de ida con equipos asignados
 * @param {Object} partidoVuelta - Partido de vuelta a actualizar
 * @param {string} campo - 'equipoLocal' o 'equipoVisitante'
 * @returns {Object} Partido de vuelta actualizado
 */
export const ReplicarEquipoIdaVuelta = (partidoIda, partidoVuelta, campo) => {
  if (!partidoIda || !partidoVuelta) {
    return partidoVuelta;
  }

  const partidoActualizado = { ...partidoVuelta };

  // El local de ida se convierte en visitante de vuelta, y viceversa
  if (campo === 'equipoLocal') {
    partidoActualizado.equipoVisitante = partidoIda.equipoLocal;
  } else if (campo === 'equipoVisitante') {
    partidoActualizado.equipoLocal = partidoIda.equipoVisitante;
  }

  return partidoActualizado;
};

/**
 * Ordena partidos por ordenPartido
 * @param {Array} partidos - Array de partidos
 * @returns {Array} Array ordenado
 */
export const OrdenarPartidos = (partidos) => {
  return [...partidos].sort((a, b) => (a.ordenPartido || 0) - (b.ordenPartido || 0));
};

/**
 * Calcula cuántos partidos se esperan en la primera fecha de una etapa de grupos
 * @param {Object} etapa - Objeto etapa con cantidadCruces
 * @returns {number} Cantidad total de partidos esperados en fecha 1
 */
export const CantidadPartidosEsperadosGrupos = (etapa) => {
  if (!etapa || etapa.definicion === true || !etapa.cantidadCruces) {
    return 0;
  }
  return etapa.cantidadCruces;
};

/**
 * Identifica qué partidos faltan en la primera fecha de una etapa de grupos
 * @param {Array} partidosExistentes - Partidos ya creados en la base de datos
 * @param {Object} contexto - Datos del contexto (torneo, categoría, etapa)
 * @param {number} nroFecha - Fecha a completar (por defecto 1)
 * @returns {Array} Array de partidos vacíos para los que faltan
 */
export const PartidosFaltantesGrupos = (partidosExistentes, contexto, nroFecha = 1) => {
  const etapa = contexto?.etapa;
  if (!etapa || etapa.definicion === true) {
    return [];
  }

  const existentes = (partidosExistentes || []).filter(
    (partido) => Number(partido.nroFecha) === Number(nroFecha)
  );
  const cantidadCruces = etapa.cantidadCruces || 0;
  const faltantesCantidad = Math.max(0, cantidadCruces - existentes.length);

  const faltantes = [];
  let idTemporal = -1;

  for (let i = 0; i < faltantesCantidad; i++) {
    faltantes.push(
      DTOs.PartidoFixtureVacio(contexto, idTemporal--, Instancia.UNICO, i, null, nroFecha)
    );
  }

  return faltantes;
};

/**
 * Normaliza una zona a un único carácter alfanumérico en mayúsculas (o null)
 * @param {string} zona
 * @returns {string|null}
 */
export const NormalizarZona = (zona) => {
  if (!zona) {
    return null;
  }

  const valor = zona
    .toString()
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  return valor ? valor.slice(0, 1) : null;
};

/**
 * Valida que la zona sea un único carácter alfanumérico
 * @param {string} zona
 * @returns {boolean}
 */
export const EsZonaValida = (zona) => {
  return /^[A-Z0-9]{1}$/.test(zona || '');
};

/**
 * Indica si el partido tiene cargados sus rivales (equipo local + visitante, o equipo libre)
 * @param {Object} partido
 * @returns {boolean}
 */
export const TieneRivalesValidos = (partido) => {
  return !!partido?.equipoLocal && (partido?.equipoLibre === true || !!partido?.equipoVisitante);
};

/**
 * Valida los datos mínimos de un partido del fixture (compartido entre etapas de grupos y definición)
 * @param {Object} partido
 * @param {Object} opciones - { requiereFecha, zonaObligatoria, permiteEquipoLibre }
 * @returns {string} Mensaje de error, o '' si es válido
 */
export const ValidarPartidoFixture = (partido, opciones = {}) => {
  const { requiereFecha = true, zonaObligatoria = false, permiteEquipoLibre = false } = opciones;

  if (requiereFecha && !partido.anioPartido) {
    return 'Debe ingresar la fecha del partido';
  }
  if (!partido.equipoLocal) {
    return 'Debe seleccionar el equipo local';
  }

  const necesitaVisitante = !(permiteEquipoLibre && partido.equipoLibre === true);
  if (necesitaVisitante && !partido.equipoVisitante) {
    return 'Debe seleccionar el equipo visitante';
  }
  if (necesitaVisitante && partido.equipoLocal.id === partido.equipoVisitante.id) {
    return 'Error: El equipo local y visitante no pueden ser el mismo.';
  }

  if (zonaObligatoria) {
    const zona = NormalizarZona(partido.zona);
    if (!zona || !EsZonaValida(zona)) {
      return 'Error: Debe ingresar una zona alfanumérica de 1 carácter.';
    }
  }

  return '';
};

/**
 * Valida que ningún equipo se repita entre los partidos a validar y los de referencia
 * (misma fecha, en grupos; misma instancia, en definición)
 * @param {Array} partidosAValidar
 * @param {Array} partidosReferencia
 * @param {string} etiqueta - Texto para el mensaje de error ("fecha", "instancia", etc.)
 * @returns {string} Mensaje de error, o '' si no hay repetidos
 */
export const ValidarEquiposRepetidos = (
  partidosAValidar,
  partidosReferencia = [],
  etiqueta = 'fecha'
) => {
  const equiposUsados = new Set();

  for (const partido of partidosAValidar) {
    const localId = partido.equipoLocal?.id;
    const visitanteId = partido.equipoVisitante?.id;

    if (!localId) {
      continue;
    }

    if (equiposUsados.has(localId) || (visitanteId && equiposUsados.has(visitanteId))) {
      return `Error: Un equipo no puede jugar más de un partido en la misma ${etiqueta}.`;
    }

    equiposUsados.add(localId);
    if (visitanteId) {
      equiposUsados.add(visitanteId);
    }
  }

  for (const partido of partidosReferencia) {
    const localId = partido.equipoLocal?.id;
    const visitanteId = partido.equipoVisitante?.id;
    if (!localId) {
      continue;
    }

    if (equiposUsados.has(localId) || (visitanteId && equiposUsados.has(visitanteId))) {
      return `Error: Uno de los equipos ya está asignado a otro partido de la ${etiqueta}.`;
    }
  }

  return '';
};

/**
 * Verifica si un partido candidato (recién generado) coincide con un partido ya grabado
 * (usado para detectar qué partidos del fixture automático quedaron pendientes)
 * @param {Object} candidato
 * @param {Object} existente
 * @returns {boolean}
 */
export const CoincideConPartidoGrabado = (candidato, existente) => {
  if (!candidato || !existente) {
    return false;
  }

  if (Number(candidato.nroFecha) !== Number(existente.nroFecha)) {
    return false;
  }

  if (candidato.equipoLocal?.id !== existente.equipoLocal?.id) {
    return false;
  }

  const zonaCandidata = NormalizarZona(candidato.zona);
  const zonaExistente = NormalizarZona(existente.zona);
  if ((zonaCandidata || null) !== (zonaExistente || null)) {
    return false;
  }

  if (candidato.equipoLibre === true) {
    return existente.equipoLibre === true;
  }

  return candidato.equipoVisitante?.id === existente.equipoVisitante?.id;
};

/**
 * Ordena partidos del preview del fixture automático por fecha, zona y orden
 * @param {Array} partidos
 * @returns {Array}
 */
export const OrdenarPreviewFixture = (partidos) => {
  return [...(partidos || [])].sort((a, b) => {
    const fechaA = Number(a.nroFecha || 0);
    const fechaB = Number(b.nroFecha || 0);
    if (fechaA !== fechaB) {
      return fechaA - fechaB;
    }
    const zonaA = (a.zona || '').toString();
    const zonaB = (b.zona || '').toString();
    if (zonaA !== zonaB) {
      return zonaA.localeCompare(zonaB);
    }
    return Number(a.orden || a.ordenPartido || 0) - Number(b.orden || b.ordenPartido || 0);
  });
};

/**
 * Mapea la descripción de instancia devuelta por el backend ("Unico"/"Ida"/"Vuelta") a su código (U/I/V)
 * @param {string} instancia
 * @returns {string|null}
 */
export const MapearInstanciaACodigo = (instancia) => {
  if (!instancia) {
    return null;
  }
  const desc = instancia.toLowerCase();
  if (desc === 'unico') return Instancia.UNICO;
  if (desc === 'ida') return Instancia.IDA;
  if (desc === 'vuelta') return Instancia.VUELTA;
  return instancia; // por si ya viene como código
};
